/**
 * Vehicle Catalog Selector Component
 *
 * Cascading dropdown selector for choosing vehicles from the catalog.
 * Flow: Brand → Model → Motorization → Year → Version
 *
 * @version 1.0.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Car, Fuel, Calendar, Zap, ChevronDown, Loader2, Search, X } from 'lucide-react';
import { vehicleCatalogAPI, truckCatalogAPI } from '../../api.js';

// Dropdown component with loading state
function CascadeDropdown({
  label,
  icon: Icon,
  value,
  options,
  onChange,
  placeholder,
  disabled,
  loading,
  displayKey = null,
  valueKey = null,
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
        {Icon && <Icon className="w-4 h-4" />}
        {label}
      </label>
      <div className="relative">
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled || loading}
          className={`
            w-full px-3 py-2 pr-10 bg-slate-800 border border-slate-600 rounded-lg
            text-white appearance-none cursor-pointer
            focus:ring-2 focus:ring-amber-500 focus:border-transparent
            disabled:opacity-50 disabled:cursor-not-allowed
            ${loading ? 'animate-pulse' : ''}
          `}
        >
          <option value="">{loading ? t('common.states.loading') : placeholder}</option>
          {options.map((opt, idx) => {
            const optValue = valueKey ? opt[valueKey] : (typeof opt === 'object' ? opt.id || opt.value : opt);
            let optLabel = displayKey ? opt[displayKey] : (typeof opt === 'object' ? opt.name || opt.label || opt.model_name || opt.brand_name : opt);
            // Strip brand prefix from display label (catalog data has "Citroen C2" as model_name when brand is "Citroen")
            if (opt._stripPrefix && typeof optLabel === 'string' && optLabel.toLowerCase().startsWith(opt._stripPrefix.toLowerCase() + ' ')) {
              optLabel = optLabel.substring(opt._stripPrefix.length + 1);
            }
            return (
              <option key={idx} value={optValue}>
                {optLabel}
              </option>
            );
          })}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {loading ? (
            <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </div>
    </div>
  );
}

// Main Vehicle Selector Component
export function VehicleCatalogSelector({
  onSelect,
  vehicleType = 'car', // 'car' or 'truck'
  initialBrand = null,
  showVersionDetails = true,
  className = '',
}) {
  const { t } = useTranslation();
  // State for cascading selections
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [motorizations, setMotorizations] = useState([]);
  const [years, setYears] = useState([]);
  const [versions, setVersions] = useState([]);

  // Selected values
  const [selectedBrand, setSelectedBrand] = useState(initialBrand);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedMotorization, setSelectedMotorization] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);

  // Loading states
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingMotorizations, setLoadingMotorizations] = useState(false);
  const [loadingYears, setLoadingYears] = useState(false);
  const [, setLoadingVersions] = useState(false);

  // Quick search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [, setSearchLoading] = useState(false);

  // Load brands on mount
  useEffect(() => {
    const loadBrands = async () => {
      setLoadingBrands(true);
      try {
        const api = vehicleType === 'truck' ? truckCatalogAPI : vehicleCatalogAPI;
        const response = await api.getBrands({ limit: 200 });
        if (response.ok) {
          setBrands(response.data);
        }
      } catch (error) {
        console.error('Error loading brands:', error);
      } finally {
        setLoadingBrands(false);
      }
    };
    loadBrands();
  }, [vehicleType]);

  // Load models when brand changes
  useEffect(() => {
    if (!selectedBrand) {
      setModels([]);
      setSelectedModel(null);
      return;
    }

    const loadModels = async () => {
      setLoadingModels(true);
      setSelectedModel(null);
      setMotorizations([]);
      setYears([]);
      setVersions([]);

      try {
        if (vehicleType === 'truck') {
          const response = await truckCatalogAPI.getModels({ brand: selectedBrand, limit: 200 });
          if (response.ok) {
            // Extract unique model names for trucks
            const uniqueModels = [...new Set(response.data.map(m => m.model_name))];
            setModels(uniqueModels.map(name => ({ model_name: name })));
          }
        } else {
          const response = await vehicleCatalogAPI.cascade.getModels(selectedBrand);
          if (response.ok) {
            // Tag models with brand for prefix stripping in dropdown display
            setModels(response.data.map(m => ({ ...m, _stripPrefix: selectedBrand })));
          }
        }
      } catch (error) {
        console.error('Error loading models:', error);
      } finally {
        setLoadingModels(false);
      }
    };
    loadModels();
  }, [selectedBrand, vehicleType]);

  // Load motorizations when model changes (cars only)
  useEffect(() => {
    if (!selectedBrand || !selectedModel || vehicleType === 'truck') {
      setMotorizations([]);
      setSelectedMotorization(null);
      return;
    }

    const loadMotorizations = async () => {
      setLoadingMotorizations(true);
      setSelectedMotorization(null);
      setYears([]);
      setVersions([]);

      try {
        const response = await vehicleCatalogAPI.cascade.getMotorizations(selectedBrand, selectedModel);
        if (response.ok) {
          setMotorizations(response.data);
        }
      } catch (error) {
        console.error('Error loading motorizations:', error);
      } finally {
        setLoadingMotorizations(false);
      }
    };
    loadMotorizations();
  }, [selectedBrand, selectedModel, vehicleType]);

  // Load years when motorization changes
  useEffect(() => {
    if (!selectedBrand || !selectedModel) {
      setYears([]);
      setSelectedYear(null);
      return;
    }

    // For cars, wait for motorization; for trucks, load immediately
    if (vehicleType === 'car' && !selectedMotorization) {
      setYears([]);
      setSelectedYear(null);
      return;
    }

    const loadYears = async () => {
      setLoadingYears(true);
      setSelectedYear(null);
      setVersions([]);

      try {
        const response = await vehicleCatalogAPI.cascade.getYears(selectedBrand, selectedModel);
        if (response.ok) {
          setYears(response.data);
        }
      } catch (error) {
        console.error('Error loading years:', error);
      } finally {
        setLoadingYears(false);
      }
    };
    loadYears();
  }, [selectedBrand, selectedModel, selectedMotorization, vehicleType]);

  // Load versions when all criteria selected
  useEffect(() => {
    if (!selectedBrand || !selectedModel) {
      setVersions([]);
      setSelectedVersion(null);
      return;
    }

    const loadVersions = async () => {
      setLoadingVersions(true);
      setSelectedVersion(null);

      try {
        const params = {
          brand: selectedBrand,
          model: selectedModel,
        };
        if (selectedMotorization) params.motorization = selectedMotorization;
        if (selectedYear) params.year = selectedYear;

        const response = await vehicleCatalogAPI.cascade.getVersions(params);
        if (response.ok) {
          setVersions(response.data);
        }
      } catch (error) {
        console.error('Error loading versions:', error);
      } finally {
        setLoadingVersions(false);
      }
    };

    // Only load versions after year is selected (or motorization for trucks)
    if (selectedYear || (vehicleType === 'truck' && selectedModel)) {
      loadVersions();
    }
  }, [selectedBrand, selectedModel, selectedMotorization, selectedYear, vehicleType]);

  // Handle version selection
  const handleVersionSelect = useCallback((versionId) => {
    const version = versions.find(v => v.id === parseInt(versionId, 10));
    setSelectedVersion(version);
    if (version && onSelect) {
      // Strip brand prefix from model_name if present
      let cleanModel = version.model_name;
      if (version.brand_name && cleanModel.toLowerCase().startsWith(version.brand_name.toLowerCase() + ' ')) {
        cleanModel = cleanModel.substring(version.brand_name.length + 1);
      }
      onSelect({
        catalogId: version.id,
        brand: version.brand_name,
        model: cleanModel,
        version: version.version_name,
        year: version.year,
        motorization: version.motorization,
        fiscalPower: version.fiscal_power,
        powerHp: version.power_hp,
        bodyType: version.body_type,
        doors: version.doors,
        seats: version.seats,
        price: version.price_eur,
        co2: version.co2_emission_wltp,
        electricRange: version.electric_range_km,
        tankCapacity: version.tank_capacity_l,
      });
    }
  }, [versions, onSelect]);

  // Quick search handler
  const handleQuickSearch = useCallback(async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults(null);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await vehicleCatalogAPI.quickSearch(query, 15);
      if (response.ok) {
        setSearchResults(response);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Select from search results
  const handleSearchSelect = useCallback((result) => {
    if (result.type === 'brand') {
      setSelectedBrand(result.name);
      setSearchQuery('');
      setSearchResults(null);
    } else if (result.type === 'model') {
      setSelectedBrand(result.brand_name);
      setTimeout(() => setSelectedModel(result.name.replace(result.brand_name + ' ', '')), 100);
      setSearchQuery('');
      setSearchResults(null);
    } else if (result.type === 'version') {
      // Direct version selection - fetch full details
      vehicleCatalogAPI.getVersionById(result.id).then(response => {
        if (response.ok && onSelect) {
          const v = response.data;
          let cleanModel = v.model_name;
          if (v.brand_name && cleanModel.toLowerCase().startsWith(v.brand_name.toLowerCase() + ' ')) {
            cleanModel = cleanModel.substring(v.brand_name.length + 1);
          }
          onSelect({
            catalogId: v.id,
            brand: v.brand_name,
            model: cleanModel,
            version: v.version_name,
            year: v.year,
            motorization: v.motorization,
            fiscalPower: v.fiscal_power,
            powerHp: v.power_hp,
            bodyType: v.body_type,
            doors: v.doors,
            seats: v.seats,
            price: v.price_eur,
            co2: v.co2_emission_wltp,
            electricRange: v.electric_range_km,
          });
        }
      });
      setSearchQuery('');
      setSearchResults(null);
    }
  }, [onSelect]);

  // Clear selection
  const clearSelection = () => {
    setSelectedBrand(null);
    setSelectedModel(null);
    setSelectedMotorization(null);
    setSelectedYear(null);
    setSelectedVersion(null);
    setVersions([]);
    if (onSelect) onSelect(null);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Quick Search */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleQuickSearch(e.target.value)}
            placeholder={t('common.catalog.searchPlaceholder')}
            className="w-full pl-10 pr-10 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(''); setSearchResults(null); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Search Results Dropdown */}
        {searchResults && (
          <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-64 overflow-y-auto">
            {searchResults.brands.length > 0 && (
              <div className="p-2 border-b border-slate-700">
                <div className="text-xs text-slate-400 px-2 py-1">{t('common.catalog.searchGroups.brands')}</div>
                {searchResults.brands.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => handleSearchSelect(b)}
                    className="w-full text-left px-3 py-2 text-white hover:bg-slate-700 rounded"
                  >
                    {b.name}
                  </button>
                ))}
              </div>
            )}
            {searchResults.models.length > 0 && (
              <div className="p-2 border-b border-slate-700">
                <div className="text-xs text-slate-400 px-2 py-1">{t('common.catalog.searchGroups.models')}</div>
                {searchResults.models.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => handleSearchSelect(m)}
                    className="w-full text-left px-3 py-2 text-white hover:bg-slate-700 rounded"
                  >
                    {m.name}
                  </button>
                ))}
              </div>
            )}
            {searchResults.versions.length > 0 && (
              <div className="p-2">
                <div className="text-xs text-slate-400 px-2 py-1">{t('common.catalog.searchGroups.versions')}</div>
                {searchResults.versions.slice(0, 5).map((v) => (
                  <button
                    key={v.id}
                    onClick={() => handleSearchSelect(v)}
                    className="w-full text-left px-3 py-2 text-white hover:bg-slate-700 rounded text-sm"
                  >
                    <div>{v.name}</div>
                    <div className="text-xs text-slate-400">{v.year} - {v.motorization} - {v.power_hp}{t('common.catalog.horsepowerUnit')}</div>
                  </button>
                ))}
              </div>
            )}
            {searchResults.total === 0 && (
              <div className="p-4 text-center text-slate-400">{t('common.states.noResult')}</div>
            )}
          </div>
        )}
      </div>

      {/* Cascading Dropdowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Brand */}
        <CascadeDropdown
          label={t('common.catalog.brand')}
          icon={Car}
          value={selectedBrand}
          options={brands}
          onChange={setSelectedBrand}
          placeholder={t('common.catalog.selectBrand')}
          loading={loadingBrands}
          displayKey="brand_name"
          valueKey="brand_name"
        />

        {/* Model */}
        <CascadeDropdown
          label={t('common.catalog.model')}
          icon={Car}
          value={selectedModel}
          options={models}
          onChange={setSelectedModel}
          placeholder={t('common.catalog.selectModel')}
          disabled={!selectedBrand}
          loading={loadingModels}
          displayKey="model_name"
          valueKey="model_name"
        />

        {/* Motorization (cars only) */}
        {vehicleType === 'car' && (
          <CascadeDropdown
            label={t('common.catalog.energy')}
            icon={Fuel}
            value={selectedMotorization}
            options={motorizations}
            onChange={setSelectedMotorization}
            placeholder={t('common.catalog.selectEnergy')}
            disabled={!selectedModel}
            loading={loadingMotorizations}
          />
        )}

        {/* Year */}
        <CascadeDropdown
          label={t('common.catalog.year')}
          icon={Calendar}
          value={selectedYear}
          options={years}
          onChange={setSelectedYear}
          placeholder={t('common.catalog.selectYear')}
          disabled={vehicleType === 'car' ? !selectedMotorization : !selectedModel}
          loading={loadingYears}
        />
      </div>

      {/* Version Selection */}
      {versions.length > 0 && (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
            <Zap className="w-4 h-4" />
            {t('common.catalog.versionsCount', { count: versions.length })}
          </label>
          <select
            value={selectedVersion?.id || ''}
            onChange={(e) => handleVersionSelect(e.target.value)}
            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-amber-500"
          >
            <option value="">{t('common.catalog.selectVersion')}</option>
            {versions.map((v) => {
              let vName = v.version_name;
              // Strip brand prefix from version_name
              if (selectedBrand && vName.toLowerCase().startsWith(selectedBrand.toLowerCase() + ' ')) {
                vName = vName.substring(selectedBrand.length + 1);
              }
              return (
                <option key={v.id} value={v.id}>
                  {vName} - {v.power_hp}{t('common.catalog.horsepowerUnit')}
                  {v.price_eur ? ` - ${v.price_eur.toLocaleString()} EUR` : ''}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {/* Selected Version Details */}
      {showVersionDetails && selectedVersion && (
        <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-white font-medium">
              {selectedVersion.brand_name} {selectedVersion.model_name.toLowerCase().startsWith(selectedVersion.brand_name.toLowerCase() + ' ')
                ? selectedVersion.model_name.substring(selectedVersion.brand_name.length + 1)
                : selectedVersion.model_name}
            </h4>
            <button
              onClick={clearSelection}
              className="text-slate-400 hover:text-red-400 p-1"
              title={t('common.catalog.clearSelection')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-amber-400 text-sm mb-3">{selectedVersion.version_name}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-slate-400">{t('common.catalog.year')}</span>
              <p className="text-white">{selectedVersion.year}</p>
            </div>
            <div>
              <span className="text-slate-400">{t('common.catalog.energy')}</span>
              <p className="text-white">{selectedVersion.motorization}</p>
            </div>
            <div>
              <span className="text-slate-400">{t('common.catalog.power')}</span>
              <p className="text-white">{selectedVersion.power_hp} {t('common.catalog.horsepowerUnit')}</p>
            </div>
            <div>
              <span className="text-slate-400">{t('common.catalog.fiscalPower')}</span>
              <p className="text-white">{selectedVersion.fiscal_power || '-'}</p>
            </div>
            {selectedVersion.price_eur && (
              <div>
                <span className="text-slate-400">{t('common.catalog.price')}</span>
                <p className="text-white">{selectedVersion.price_eur.toLocaleString()} EUR</p>
              </div>
            )}
            {selectedVersion.co2_emission_wltp && (
              <div>
                <span className="text-slate-400">CO2</span>
                <p className="text-white">{selectedVersion.co2_emission_wltp} g/km</p>
              </div>
            )}
            {selectedVersion.electric_range_km && (
              <div>
                <span className="text-slate-400">{t('common.catalog.range')}</span>
                <p className="text-white">{selectedVersion.electric_range_km} km</p>
              </div>
            )}
            {selectedVersion.body_type && (
              <div>
                <span className="text-slate-400">{t('common.catalog.bodyType')}</span>
                <p className="text-white">{selectedVersion.body_type}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default VehicleCatalogSelector;
